import { Link } from '@tanstack/react-router';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { usePrototypesQuery } from '@/features/prototypes/prototypes-queries';
import { validatePrototypesSearch } from '@/features/prototypes/prototypes-search';

export interface PrototypeRailProps {
  readonly projectId: string;
  /** The prototype currently on display, highlighted in the list. */
  readonly currentPrototypeId: string;
  readonly collapsed: boolean;
  readonly onToggle: () => void;
}

/**
 * A collapsible left rail of shortcuts to every prototype in the same project,
 * so a reviewer can jump between siblings without going back to the project
 * page. It reads the first page of the project's prototypes (the same list the
 * project page shows, most-recently-updated first); a project with more than one
 * page links out to the full list.
 *
 * Styled to match the viewer chrome, and a sibling of the canvas rather than an
 * overlay, so — like the thread panel — it takes its width out of the space the
 * prototype is measured in instead of covering it.
 */
export const PrototypeRail = ({
  projectId,
  currentPrototypeId,
  collapsed,
  onToggle,
}: PrototypeRailProps) => {
  // A fixed default view: newest first, first page. The rail is a shortcut list,
  // not the searchable browser, so it carries no controls of its own.
  const search = useMemo(() => validatePrototypesSearch({}), []);
  const query = usePrototypesQuery(projectId, search);

  if (collapsed) {
    return (
      <div className="flex w-10 shrink-0 flex-col items-center border-r border-viewer-chrome-border bg-viewer-chrome py-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 text-viewer-chrome-foreground hover:text-foreground"
              onClick={onToggle}
              aria-label="Show the prototype list"
            >
              <PanelLeftOpen aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Prototypes</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  const prototypes = query.data?.items ?? [];
  const hasMore =
    query.data !== undefined && query.data.total > prototypes.length;

  return (
    <aside className="flex w-56 shrink-0 flex-col border-r border-viewer-chrome-border bg-viewer-chrome">
      <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-viewer-chrome-border px-3">
        <span className="truncate text-xs font-semibold uppercase tracking-wide text-viewer-chrome-foreground">
          Prototypes
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 shrink-0 text-viewer-chrome-foreground hover:text-foreground"
              onClick={onToggle}
              aria-label="Hide the prototype list"
            >
              <PanelLeftClose aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Collapse</TooltipContent>
        </Tooltip>
      </div>

      <nav
        aria-label="Prototypes in this project"
        className="min-h-0 flex-1 overflow-y-auto p-2"
      >
        {query.isPending ? (
          <ul className="space-y-1">
            {[0, 1, 2, 3].map((row) => (
              <li key={row}>
                <Skeleton className="h-8 w-full rounded-md" />
              </li>
            ))}
          </ul>
        ) : query.isError ? (
          <p className="px-2 py-1.5 text-xs text-muted-foreground">
            Couldn&apos;t load the prototype list.
          </p>
        ) : (
          <ul className="space-y-0.5">
            {prototypes.map((prototype) => {
              const isCurrent = prototype.id === currentPrototypeId;
              return (
                <li key={prototype.id}>
                  <Link
                    to="/prototypes/$prototypeId"
                    params={{ prototypeId: prototype.id }}
                    aria-current={isCurrent ? 'page' : undefined}
                    className={cn(
                      'flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm',
                      isCurrent
                        ? 'bg-viewer-chrome-border/60 font-medium text-foreground'
                        : 'text-viewer-chrome-foreground hover:bg-viewer-chrome-border/40 hover:text-foreground',
                    )}
                  >
                    <span className="min-w-0 truncate">{prototype.name}</span>
                    {prototype.openThreadCount > 0 ? (
                      <span
                        className="shrink-0 rounded-full bg-pin px-1.5 text-xs font-medium leading-5 text-pin-foreground"
                        title={`${String(prototype.openThreadCount)} open comment${
                          prototype.openThreadCount === 1 ? '' : 's'
                        }`}
                      >
                        {prototype.openThreadCount}
                      </span>
                    ) : null}
                  </Link>
                </li>
              );
            })}

            {hasMore ? (
              <li className="pt-1">
                <Link
                  to="/projects/$projectId"
                  params={{ projectId }}
                  className="block rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground"
                >
                  View all prototypes…
                </Link>
              </li>
            ) : null}
          </ul>
        )}
      </nav>
    </aside>
  );
};
